import React, { useMemo, useCallback, memo } from 'react';
import { format } from 'date-fns';
import { TIME_PART_ICON_BY_ID } from '../../booking-time-parts/TimePartIcons';

const TIME_SLOT_DAY_PART_ORDER = ['morning', 'afternoon', 'evening'];
const TIME_SLOT_DAY_PART_LABELS = {
  morning: 'Morning',
  afternoon: 'Afternoon',
  evening: 'Evening',
};

const getTimeSlotDayPart = (slot) => {
  const hour = slot.getHours();
  if (hour < 12) return 'morning';
  if (hour < 17) return 'afternoon';
  return 'evening';
};

const groupTimeSlotsByDayPart = (slots) => {
  const groups = { morning: [], afternoon: [], evening: [] };
  for (const slot of slots) {
    groups[getTimeSlotDayPart(slot)].push(slot);
  }
  return groups;
};

const TimeSlotButton = memo(function TimeSlotButton({ slot, selected, onSelect }) {
  const handleClick = useCallback(() => onSelect(slot), [onSelect, slot]);

  return (
    <button
      type="button"
      className={`time-slot ${selected ? 'selected' : ''}`}
      onClick={handleClick}
    >
      {format(slot, 'h:mm a')}
    </button>
  );
});

const TimeSlotPeriod = memo(function TimeSlotPeriod({ part, slots, selectedTime, onSelectTime }) {
  if (!slots.length) return null;

  const Icon = TIME_PART_ICON_BY_ID[part];
  const selectedTimeMs = selectedTime?.getTime() ?? null;

  return (
    <div className="time-slots-period">
      <div className="time-slots-period__label">
        <Icon className="time-slots-period__icon" />
        <span>{TIME_SLOT_DAY_PART_LABELS[part]}</span>
      </div>
      <div className="time-slots">
        {slots.map((slot) => (
          <TimeSlotButton
            key={slot.getTime()}
            slot={slot}
            selected={selectedTimeMs === slot.getTime()}
            onSelect={onSelectTime}
          />
        ))}
      </div>
    </div>
  );
});

const TimeSelectionStep = ({
  date,
  time,
  setTime,
  availableSlots,
  loading,
  slotsFetched = false,
  error,
  customerNoStaffMessage,
  onNext,
  onBack,
  onCancel,
}) => {
  const slotsByDayPart = useMemo(
    () => groupTimeSlotsByDayPart(availableSlots),
    [availableSlots]
  );

  if (error) {
    return (
      <div className="booking-step booking-step--time">
        <h3>Select Time</h3>
        <p className="booking-emptyStateMessage booking-step--time__message">{error}</p>
        <div className="button-group">
          <button type="button" className="btn-secondary" onClick={onBack}>
            Back
          </button>
          {error === customerNoStaffMessage ? (
            <button type="button" className="btn-primary" onClick={onCancel}>
              Choose Another Service
            </button>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div className="booking-step booking-step--time">
      <h3>Select Time</h3>
      {date && (
        <p className="booking-step--time__date">{format(date, 'EEEE, MMMM d')}</p>
      )}

      {loading && <div className="loading">Searching available times…</div>}

      {date && availableSlots.length > 0 && (
        <div className="time-slots-grouped">
          {TIME_SLOT_DAY_PART_ORDER.map((part) => (
            <TimeSlotPeriod
              key={part}
              part={part}
              slots={slotsByDayPart[part]}
              selectedTime={time}
              onSelectTime={setTime}
            />
          ))}
        </div>
      )}

      {date && slotsFetched && availableSlots.length === 0 && !loading && (
        <p className="no-slots">No available time slots for this date.</p>
      )}

      <div className="button-group">
        <button type="button" className="btn-secondary" onClick={onBack}>
          Back
        </button>
        <button type="button" className="btn-primary" onClick={onNext} disabled={!date || !time}>
          Continue
        </button>
      </div>
    </div>
  );
};

export default memo(TimeSelectionStep);
